import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { Upload, FileText, Image, Trash2 } from 'lucide-react';
import domainConfig from './config/domainConfig';

// Available report types - moved outside component to prevent recreating on every render
const reportTypes = [
    {
      id: 'goalline',
      name: 'Goal Line Analysis Report',
      description: 'Analyze offensive tendencies within 5 yards of the goal line',
      icon: <FileText size={24} />,
      path: '/goalline',
      color: 'primary'
    },
    {
      id: 'finaldowns',
      name: 'Final Downs Report',
      description: 'Analyze true final downs - 3rd downs followed by 4th downs',
      icon: <FileText size={24} />,
      path: '/finaldowns',
      color: 'secondary'
    },
    {
      id: 'passingtargets',
      name: 'Passing Targets Report',
      description: 'Analyze passing target distribution and deep ball usage by receiver',
      icon: <FileText size={24} />,
      path: '/passingtargets',
      color: 'info'
    },
    {
      id: 'defensivevulnerability',
      name: 'Defensive Vulnerability Report',
      description: 'Analyze defensive weaknesses through explosive runs (10+ yds) and passes (15+ yds)',
      icon: <FileText size={24} />,
      path: '/defensivevulnerability',
      color: 'warning'
    },
    {
      id: 'twominute',
      name: 'Two Minute Report',
      description: 'Analyze offensive tendencies in critical 2-minute situations by quarter',
      icon: <FileText size={24} />,
      path: '/twominute',
      color: 'error'
    },
    {
      id: 'redzone',
      name: 'Red Zone Report',
      description: 'Analyze offensive tendencies in red zone (6-20 yard line) on base downs',
      icon: <FileText size={24} />,
      path: '/redzone',
      color: 'success'
    },
    {
      id: 'basedowns',
      name: 'Base Downs Report',
      description: 'Analyze offensive tendencies on 1st and 2nd downs by personnel groupings',
      icon: <FileText size={24} />,
      path: '/basedowns',
      color: 'primary'
    },
    {
      id: 'vangobasedowns',
      name: 'VanGo Base Downs Analysis',
      description: 'Analyze personnel groupings, formations, run concepts, and defensive takeaways from Excel data',
      icon: <FileText size={24} />,
      path: '/vangobasedowns',
      color: 'secondary'
    },
    {
      id: 'vangofinaldowns',
      name: 'VanGo Final Downs Analysis',
      description: 'Analyze true final downs tendencies when defense knows offense MUST convert or give up possession',
      icon: <FileText size={24} />,
      path: '/vangofinaldowns',
      color: 'info'
    },
    {
      id: 'vangotwominutes',
      name: 'VanGo 2-Minute Analysis',
      description: 'Analyze offensive tendencies in critical 2-minute situations (Q2 and Q4) with personnel and passing breakdowns',
      icon: <FileText size={24} />,
      path: '/vangotwominutes',
      color: 'warning'
    }
    // Add more report types here as they are developed
  ];

const Reports = ({ user }) => {
  const [logos, setLogos] = useState({ clientLogo: null, companyLogo: null });
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [allowedReports, setAllowedReports] = useState([]);

  const loadAllowedReports = useCallback(async () => {
    console.log('🔍 loadAllowedReports - User object:', user);
    console.log('🔍 loadAllowedReports - User team_level:', user?.team_level);
    console.log('🔍 loadAllowedReports - User isAdmin:', user?.isAdmin);

    // If user is admin, show all reports
    if (user?.isAdmin) {
      setAllowedReports(reportTypes);
      return;
    }

    // If user has no team level, show no reports
    if (!user?.team_level) {
      console.log('❌ No team level found for user');
      setAllowedReports([]);
      return;
    }

    try {
      const response = await fetch(`${domainConfig.apiUrl}/api/user/allowed-reports`, {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 /api/user/allowed-reports response:', data);
        const allowedReportIds = data.allowedReports || [];
        console.log('🔍 Allowed report IDs:', allowedReportIds);
        const filteredReports = reportTypes.filter(report => allowedReportIds.includes(report.id));
        console.log('🔍 Filtered reports:', filteredReports);
        setAllowedReports(filteredReports);
      } else {
        // If permissions endpoint fails, show all reports (fallback)
        console.error('Failed to load report permissions, showing all reports');
        setAllowedReports(reportTypes);
      }
    } catch (error) {
      // If permissions endpoint fails, show all reports (fallback)
      console.error('Error loading report permissions:', error);
      setAllowedReports(reportTypes);
    }
  }, [user]);

  useEffect(() => {
    loadUserLogos();
    loadAllowedReports();
  }, [user, loadAllowedReports]);

  const loadUserLogos = async () => {
    try {
      const response = await fetch(`${domainConfig.apiUrl}/api/logos`, {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setLogos(data.logos || {});
      } else {
        console.error('Failed to load logos');
      }
    } catch (error) {
      console.error('Error loading logos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (event, logoType) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    
    if (file.size > maxSize) {
      setError('Logo file size must be less than 5MB');
      return;
    }

    if (!allowedTypes.includes(file.type.toLowerCase())) {
      setError('Please upload a valid image file (JPG, PNG, GIF, WebP only)');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append(logoType, file);

      const response = await fetch(`${domainConfig.apiUrl}/api/logos/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        setSuccess(`${logoType === 'clientLogo' ? 'Client' : 'Company'} logo uploaded successfully!`);
        await loadUserLogos(); // Reload logos
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to upload logo');
      }
    } catch (error) {
      setError('Error uploading logo: ' + error.message);
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleLogoDelete = async (logoType) => {
    if (!window.confirm(`Are you sure you want to delete the ${logoType} logo?`)) {
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const response = await fetch(`${domainConfig.apiUrl}/api/logos/${logoType}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setSuccess(`${logoType} logo deleted successfully!`);
        await loadUserLogos(); // Reload logos
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete logo');
      }
    } catch (error) {
      setError('Error deleting logo: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <FileText size={32} />
        Reports Dashboard
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Upload CSV data to generate comprehensive reports. Configure your logos below to brand all reports.
      </Typography>

      {/* Error and Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Logo Management Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Image size={24} />
            Logo Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload logos that will appear on all generated reports. Logos are optional but help brand your reports.
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Client Logo Section - TEMPORARILY DISABLED */}
              {/*
              <Grid item xs={12} md={6}>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
                  <Typography variant="h6" gutterBottom>Client Logo</Typography>

                  {logos.clientLogo ? (
                    <Box sx={{ mb: 2 }}>
                      <img
                        src={logos.clientLogo.url}
                        alt="Client Logo"
                        style={{
                          maxHeight: '80px',
                          maxWidth: '100%',
                          objectFit: 'contain',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          padding: '8px'
                        }}
                      />
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<Trash2 size={16} />}
                          onClick={() => handleLogoDelete('client')}
                          disabled={uploading}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No client logo uploaded
                    </Typography>
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleLogoUpload(e, 'clientLogo')}
                    style={{ display: 'none' }}
                    id="client-logo-upload"
                    disabled={uploading}
                  />
                  <label htmlFor="client-logo-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={uploading ? <CircularProgress size={16} /> : <Upload size={16} />}
                      disabled={uploading}
                    >
                      {logos.clientLogo ? 'Replace' : 'Upload'} Client Logo
                    </Button>
                  </label>
                </Box>
              </Grid>
              */}

              {/* Company Logo Section - Admin Only */}
              {user?.isAdmin && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Company Logo
                    </Typography>
                    
                    {logos.companyLogo ? (
                      <Box sx={{ mb: 2 }}>
                        <img 
                          src={logos.companyLogo.url} 
                          alt="Company Logo" 
                          style={{ 
                            maxHeight: '80px', 
                            maxWidth: '100%', 
                            objectFit: 'contain',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '8px'
                          }} 
                        />
                        <Box sx={{ mt: 1 }}>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<Trash2 size={16} />}
                            onClick={() => handleLogoDelete('company')}
                            disabled={uploading}
                          >
                            Delete
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No company logo uploaded
                      </Typography>
                    )}

                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleLogoUpload(e, 'companyLogo')}
                      style={{ display: 'none' }}
                      id="company-logo-upload"
                      disabled={uploading}
                    />
                    <label htmlFor="company-logo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={uploading ? <CircularProgress size={16} /> : <Upload size={16} />}
                        disabled={uploading}
                      >
                        {logos.companyLogo ? 'Replace' : 'Upload'} Company Logo
                      </Button>
                    </label>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </CardContent>
      </Card>

      <Divider sx={{ mb: 4 }} />

      {/* Available Reports Section */}
      <Typography variant="h5" gutterBottom>Available Reports</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select a report type below to upload your CSV data and generate analysis.
      </Typography>

      {allowedReports.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          {user?.team_level
            ? `No reports are currently available for your team level (${user.team_level}). Please contact your administrator.`
            : 'No team level assigned. Please contact your administrator to assign a team level.'
          }
        </Typography>
      ) : (
        <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
          {allowedReports.map((report) => (
            <ListItem key={report.id} disablePadding>
              <ListItemButton
                component={Link}
                to={report.path}
                sx={{
                  py: 2,
                  px: 3,
                  '&:hover': {
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText',
                    transform: 'translateX(4px)',
                    transition: 'all 0.2s ease-in-out'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <ListItemText
                  primary={report.name}
                  primaryTypographyProps={{
                    variant: 'h6',
                    fontWeight: 500,
                    color: 'primary.main'
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}

      {/* Instructions */}
      <Card sx={{ mt: 4, bgcolor: 'background.default' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>How to Use</Typography>
          <Typography variant="body2" component="div">
            <ol>
              {/* <li><strong>Upload Logos (Optional):</strong> Upload your client logo above. {user?.isAdmin ? 'As an admin, you can also manage the company logo which will appear on all users\' reports. ' : 'The company logo is managed by admins and will appear on your reports when set. '}Client logos will appear on all your generated reports when uploaded.</li> */}
              <li><strong>Select Report Type:</strong> Choose the type of analysis you want to perform from the available reports above.</li>
              <li><strong>Upload CSV Data:</strong> On the report page, upload your CSV file containing the data to analyze.</li>
              <li><strong>View Results:</strong> The report will be generated server-side and displayed on the page.</li>
              <li><strong>Download PDF:</strong> Use the PDF download option to save the report for sharing or archiving.</li>
            </ol>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Reports;